import { get, post, put, del } from '../utils/networking';

export enum QuestionType {
  LIKERT_SCALE = 'LIKERT_SCALE',
  NPS_SCALE = 'NPS_SCALE',
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
  MULTIPLE_SELECT = 'MULTIPLE_SELECT',
  SHORT_TEXT = 'SHORT_TEXT',
  LONG_TEXT = 'LONG_TEXT',
  YES_NO = 'YES_NO',
}

export enum ChartType {
  BAR = 'BAR',
  HORIZONTAL_BAR = 'HORIZONTAL_BAR',
  LINE = 'LINE',
  DONUT = 'DONUT',
  NPS_GAUGE = 'NPS_GAUGE',
  NPS_BAR = 'NPS_BAR',
  CSAT_METER = 'CSAT_METER',
}

export enum TemplateScope {
  TEAM = 'TEAM',
  TENANT = 'TENANT',
}

export type QuestionOption = {
  id?: number;
  optionText: string;
  orderIndex: number;
};

export type TemplateQuestion = {
  id?: number;
  questionText: string;
  questionType: QuestionType;
  category?: string;
  orderIndex: number;
  isRequired: boolean;
  scaleMin?: number;
  scaleMax?: number;
  scaleMinLabel?: string;
  scaleMaxLabel?: string;
  chartType?: ChartType;
  options?: QuestionOption[];
};

export type CustomSurveyTemplate = {
  id: number;
  name: string;
  description?: string;
  category?: string;
  scope: TemplateScope;
  scopeTeamId?: number;
  scopeTeamName?: string | null;
  isArchived: boolean;
  version: number;
  previousVersionId?: number;
  questions: TemplateQuestion[];
  createdAt: string;
  updatedAt: string;
  /** Date when a survey was last created from this template */
  lastUsed?: string | null;
  /** Number of surveys created from this template */
  surveysCreated?: number;
  /** Whether this template was created within the last 7 days */
  isNew?: boolean;
};

export type CreateCustomSurveyTemplateRequest = {
  name: string;
  description?: string;
  category?: string;
  scope: TemplateScope;
  scopeTeamId?: number;
  questions: Omit<TemplateQuestion, 'id'>[];
};

// API calls
export const getCustomSurveyTemplates = async (params?: {
  teamId?: number;
  filterByUsage?: boolean;
}): Promise<CustomSurveyTemplate[]> => {
  const queryParams = new URLSearchParams();
  if (params?.teamId) queryParams.append('teamId', params.teamId.toString());
  if (params?.filterByUsage !== undefined)
    queryParams.append('filterByUsage', params.filterByUsage.toString());

  const url = `/api/custom-survey-templates${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  return get<CustomSurveyTemplate[]>(url);
};

export const createCustomSurveyTemplate = async (
  data: CreateCustomSurveyTemplateRequest,
): Promise<CustomSurveyTemplate> => {
  return post<CustomSurveyTemplate>('/api/custom-survey-templates', data);
};

export const getCustomSurveyTemplateById = async (
  id: number,
): Promise<CustomSurveyTemplate> => {
  return get<CustomSurveyTemplate>(`/api/custom-survey-templates/${id}`);
};

export const updateCustomSurveyTemplate = async (
  id: number,
  data: CreateCustomSurveyTemplateRequest,
): Promise<CustomSurveyTemplate> => {
  return put<CustomSurveyTemplate>(`/api/custom-survey-templates/${id}`, data);
};

export const deleteCustomSurveyTemplate = async (id: number): Promise<void> => {
  return del<void>(`/api/custom-survey-templates/${id}`);
};

export const duplicateCustomSurveyTemplate = async (
  id: number,
): Promise<CustomSurveyTemplate> => {
  return post<CustomSurveyTemplate>(`/api/custom-survey-templates/${id}/duplicate`, {});
};

export const archiveCustomSurveyTemplate = async (
  id: number,
): Promise<CustomSurveyTemplate> => {
  return post<CustomSurveyTemplate>(`/api/custom-survey-templates/${id}/archive`, {});
};

export const unarchiveCustomSurveyTemplate = async (
  id: number,
): Promise<CustomSurveyTemplate> => {
  return post<CustomSurveyTemplate>(
    `/api/custom-survey-templates/${id}/unarchive`,
    {},
  );
};

export type TemplateUsageInfo = {
  isInUse: boolean;
  surveysCount: number;
  scheduledSurveysCount: number;
};

export const checkCustomSurveyTemplateUsage = async (
  id: number,
): Promise<TemplateUsageInfo> => {
  return get<TemplateUsageInfo>(`/api/custom-survey-templates/${id}/usage`);
};

export const createCustomSurveyTemplateVersion = async (
  id: number,
  data: CreateCustomSurveyTemplateRequest,
): Promise<CustomSurveyTemplate> => {
  return post<CustomSurveyTemplate>(
    `/api/custom-survey-templates/${id}/create-version`,
    data,
  );
};
